import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { FolderKanban, MessageSquare, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { projectRoute } from '../constants';
import type { Project } from '../types';
import { ProjectDropTarget } from './ProjectDropTarget';

interface ProjectCardProps {
  project: Project;
  onDropConversation: (sessionId: string, project: Project) => void;
  onEdit: (project: Project) => void;
  onDelete: (project: Project) => void;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  onDropConversation,
  onEdit,
  onDelete,
}) => {
  const { t, i18n } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const updatedAt = new Date(project.updatedAt).toLocaleDateString(i18n.language);

  return (
    <ProjectDropTarget
      onDropConversation={(sessionId) => onDropConversation(sessionId, project)}
      className="relative rounded-xl"
    >
      <Link
        to={projectRoute(project.publicId)}
        className="flex h-full min-h-36 flex-col gap-2 rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-all hover:border-blue-400 hover:shadow-md dark:border-slate-700 dark:bg-slate-800 dark:hover:border-blue-400/60"
      >
        <div className="flex items-center gap-2 pr-8">
          <FolderKanban size={18} className="shrink-0 text-blue-500" />
          <h3 className="min-w-0 flex-1 truncate font-semibold text-gray-900 dark:text-slate-100">
            {project.name}
          </h3>
        </div>
        <p className="line-clamp-2 min-h-10 text-sm text-gray-600 dark:text-slate-400">
          {project.description || t('projects.list.noDescription')}
        </p>
        <div className="mt-auto flex items-center justify-between text-xs text-gray-500 dark:text-slate-400">
          <span className="inline-flex items-center gap-1">
            <MessageSquare size={12} />
            {t('projects.list.conversationCount', { count: project.sessionCount })}
          </span>
          <span>{t('projects.list.updatedAt', { date: updatedAt })}</span>
        </div>
      </Link>
      <IconButton
        size="small"
        aria-label={t('projects.detail.options')}
        onClick={(e) => setMenuAnchor(e.currentTarget)}
        className="!absolute right-2 top-2 text-gray-500 dark:text-slate-400"
      >
        <MoreVertical size={16} />
      </IconButton>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          dense
          onClick={() => {
            setMenuAnchor(null);
            onEdit(project);
          }}
          className="gap-2"
        >
          <Pencil size={16} />
          {t('projects.detail.edit')}
        </MenuItem>
        <MenuItem
          dense
          onClick={() => {
            setMenuAnchor(null);
            onDelete(project);
          }}
          className="gap-2 !text-red-600 dark:!text-red-400"
        >
          <Trash2 size={16} />
          {t('projects.detail.delete')}
        </MenuItem>
      </Menu>
    </ProjectDropTarget>
  );
};
